import { PUBLIC_SEO_DESCRIPTION } from '~/constants/seo'
import { formatLocation } from '~/utils/location'

export default ({ app }, inject) => {
  const getLocaleColumn = () => {
    if (typeof app.$getLocaleDbColumn === 'function') {
      return app.$getLocaleDbColumn('name')
    }

    return app.i18n?.locale === 'en' ? 'name_en' : 'name'
  }

  // SEO helper methods
  const seo = {
    // Generate meta tags for job postings
    generateJobPostingMeta(jobOffer) {
      if (!jobOffer) return {}

      const title = `${jobOffer.name} - Job Opening at ${jobOffer.organization?.name || 'apropos.jobs'}`
      const description = this.truncateText(
        jobOffer.description ||
        `${jobOffer.name} position available. ${this.getPersonalityDescription(jobOffer.personality)} Apply now through our personality-based hiring platform for a better job fit.`,
        160
      ) || PUBLIC_SEO_DESCRIPTION
      const url = `https://apropos.jobs/job-offer/${jobOffer.id}`
      const image = jobOffer.organization?.logo || '/preview.png'

      const location = formatLocation(jobOffer, {
        fallback: 'Switzerland',
        localeColumn: getLocaleColumn()
      })

      return {
        title,
        meta: [
          { hid: 'description', name: 'description', content: description },
          { hid: 'keywords', name: 'keywords', content: this.generateJobKeywords(jobOffer) },
          
          // Open Graph
          { hid: 'og:title', property: 'og:title', content: title },
          { hid: 'og:description', property: 'og:description', content: description },
          { hid: 'og:url', property: 'og:url', content: url },
          { hid: 'og:type', property: 'og:type', content: 'article' },
          { hid: 'og:image', property: 'og:image', content: image },
          
          // Twitter Card
          { hid: 'twitter:title', name: 'twitter:title', content: title },
          { hid: 'twitter:description', name: 'twitter:description', content: description },
          { hid: 'twitter:image', name: 'twitter:image', content: image },
          { hid: 'twitter:card', name: 'twitter:card', content: 'summary_large_image' },
          
          // Job-specific meta
          { name: 'job:location', content: location },
          { name: 'job:company', content: jobOffer.organization?.name || 'apropos.jobs' },
          { name: 'job:employment_type', content: jobOffer.employment_type?.name || 'Full-time' }
        ],
        link: [
          { hid: 'canonical', rel: 'canonical', href: url }
        ]
      }
    },

    // Generate meta tags for organization profiles
    generateOrganizationMeta(organization) {
      if (!organization) return {}

      const title = `${organization.name} - Company Profile on apropos.jobs`
      const description = this.truncateText(
        organization.description ||
        `Learn about ${organization.name} and discover job opportunities that match your working style. Join our talent network today.`,
        160
      ) || PUBLIC_SEO_DESCRIPTION
      const url = `https://apropos.jobs/organization/${organization.id}`
      const image = organization.logo || '/preview.png'

      return {
        title,
        meta: [
          { hid: 'description', name: 'description', content: description },
          { hid: 'keywords', name: 'keywords', content: `${organization.name}, company profile, jobs, career opportunities, recruitment` },
          
          // Open Graph
          { hid: 'og:title', property: 'og:title', content: title },
          { hid: 'og:description', property: 'og:description', content: description },
          { hid: 'og:url', property: 'og:url', content: url },
          { hid: 'og:type', property: 'og:type', content: 'profile' },
          { hid: 'og:image', property: 'og:image', content: image },
          
          // Twitter Card
          { hid: 'twitter:title', name: 'twitter:title', content: title },
          { hid: 'twitter:description', name: 'twitter:description', content: description },
          { hid: 'twitter:image', name: 'twitter:image', content: image }
        ],
        link: [
          { hid: 'canonical', rel: 'canonical', href: url }
        ]
      }
    },

    // Generate meta tags for personality assessment pages
    generatePersonalityMeta(personalityType = null) {
      const title = personalityType
        ? `${personalityType} Personality Type - Career Insights`
        : 'Personality Assessment - Discover Your Career Fit'
      const description = personalityType
        ? `Learn about the ${personalityType} personality type and discover career opportunities that align with your traits and strengths.`
        : PUBLIC_SEO_DESCRIPTION

      const url = personalityType 
        ? `https://apropos.jobs/personality/${personalityType.toLowerCase()}`
        : 'https://apropos.jobs/assessment'

      return {
        title,
        meta: [
          { hid: 'description', name: 'description', content: description },
          { hid: 'keywords', name: 'keywords', content: 'personality assessment, career fit, personality test, job fit, career insights, personality types' },
          
          // Open Graph
          { hid: 'og:title', property: 'og:title', content: title },
          { hid: 'og:description', property: 'og:description', content: description },
          { hid: 'og:url', property: 'og:url', content: url },
          { hid: 'og:type', property: 'og:type', content: 'article' },
          
          // Twitter Card
          { hid: 'twitter:title', name: 'twitter:title', content: title },
          { hid: 'twitter:description', name: 'twitter:description', content: description }
        ],
        link: [
          { hid: 'canonical', rel: 'canonical', href: url }
        ]
      }
    },

    // Helper methods
    truncateText(text, length = 160) {
      if (!text) return ''
      if (text.length <= length) return text
      return text.substring(0, length - 3) + '...'
    },

    generateJobKeywords(jobOffer) {
      const keywords = ['job opportunity', 'career', 'employment']
      
      if (jobOffer.name) keywords.push(jobOffer.name)
      if (jobOffer.organization?.name) keywords.push(jobOffer.organization.name)
      if (jobOffer.occupational_field?.name) keywords.push(jobOffer.occupational_field.name)
      const location = formatLocation(jobOffer, {
        localeColumn: getLocaleColumn()
      })
      if (location) keywords.push(location)
      if (jobOffer.employment_type?.name) keywords.push(jobOffer.employment_type.name)
      
      // Add personality-related keywords
      if (jobOffer.personality?.length) {
        keywords.push('personality fit', 'personality-based hiring')
        keywords.push(...jobOffer.personality.map(p => `${p} personality`))
      }

      return keywords.join(', ')
    },

    getPersonalityDescription(personality) {
      if (!personality?.length) return ''
      
      const descriptions = {
        red: 'Seeking driven, leadership-oriented professionals',
        yellow: 'Looking for social, collaborative team members',
        blue: 'Ideal for analytical, detail-oriented individuals',
        green: 'Perfect for supportive, steady professionals'
      }

      return descriptions[personality[0]?.toLowerCase()] || ''
    },

    // Generate breadcrumb structured data
    generateBreadcrumbs(items) {
      if (!items?.length) return null

      return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: items.map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: item.name,
          item: item.url
        }))
      }
    },

    // Generate FAQ structured data
    generateFAQ(faqs) {
      if (!faqs?.length) return null

      return {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map(faq => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer
          }
        }))
      }
    }
  }

  // Inject SEO helper into context
  inject('seo', seo)
}
